import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

import { setPortal } from 'modules/portal';
import {
  dayColor,
  makeRowColumn,
  getHeadObj,
  dataConverter,
} from 'lib/commonLib';
import { dailyTargeting } from 'lib/api/time';
import { paramConverter } from 'modules/common';
import VariousCell from 'components/molecules/MbTable/VariousCell';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

import TargetingGraph from './TargetingGraph';

const initParams = {
  pltfomTpCode: 'all',
  itlTpCode: 'all',
  sort: 'desc',
  order: 'STATS_DTTM',
  sDate: moment().format('YYYYMM01'),
  eDate: moment().format('YYYYMMDD'),
};
const Targeting = ({ location }) => {
  const { mediaScriptNo } = queryString.parse(location.search);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));

  if (typeof mediaScriptNo !== 'undefined')
    initParams.mediaScriptNo = mediaScriptNo;
  const [params, setParams] = useState(initParams);
  const [initialization, setInitialization] = useState(false);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    STATS_DTTM: {
      makeHead: (headsMap) => {
        return `${getHeadObj(headsMap, 'STATS_DTTM').label}`;
      },
      makeBody: (rowObj, headsMap) => {
        const statsDttm = getHeadObj(headsMap, 'STATS_DTTM');
        let totalCheck = rowObj.STATS_DTTM === 'total';

        const convertKeys = [
          'sDate',
          'eDate',
          'advrtsPrdtCode',
          'advrtsTpCode',
          'pltfomTpCode',
          'itlTpCode',
        ];
        let addParams = dataConverter(params, convertKeys, 'string');
        const showWindowUrl =
          '/report/media/mediatarget?isPop=1&uri=/report/media/mediatarget' +
          addParams;

        return (
          <IconAreaWrap
            text={dayColor(
              rowObj['STATS_DTTM'],
              statsDttm.type,
              statsDttm.format
            )}
            icon={!rowObj.isChild && !totalCheck && 'faExternalLink'}
            toolTipTitle={t('해당 날짜의 지면별 타게팅분포')}
            onClick={(e) => handlePortalUrl(showWindowUrl)}
          />
        );
      },
    },

    PAR_EPRS_RATE: {
      merged: true,
    },
    ORDER_CNT: {
      makeHead: (headsMap) => {
        return `${getHeadObj(headsMap, 'ORDER_CNT').label}`;
      },
      makeBody: (rowObj, headsMap) => {
        const orderCnt = getHeadObj(headsMap, 'ORDER_CNT');
        let totalCheck = rowObj.STATS_DTTM === 'total';
        const showWindowUrl =
          '/report/adver/statistics?sDate=' +
          rowObj['STATS_DTTM'] +
          '&eDate=' +
          rowObj['STATS_DTTM'] +
          '&uri=/report/adver/statistics&isPop=1';

        let resultComponent = (
          <IconAreaWrap
            text={dayColor(rowObj['ORDER_CNT'], orderCnt.type, orderCnt.format)}
            icon={!rowObj.isChild && !totalCheck && 'faExternalLink'}
            toolTipTitle={t('해당 날짜의 컨버전 내역')}
            onClick={(e) => handlePortalUrl(showWindowUrl)}
          />
        );

        return resultComponent;
      },
    },
    MOB_SESSION_ROAS: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={
                  getHeadObj(headsMap, 'MOB_SESSION_ROAS').label +
                  '(' +
                  t('쇼핑') +
                  ')'
                }
                tooltipType={'MOB_SESSION_ROAS'}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const mobSessionRoas = getHeadObj(headsMap, 'MOB_SESSION_ROAS');
        const trgtMobSessionRoas = getHeadObj(
          headsMap,
          'TRGT_MOB_SESSION_ROAS'
        );
        const mobSessionOrderAmt = getHeadObj(
          headsMap,
          'MOB_SESSION_ORDER_AMT'
        );
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(
              rowObj['MOB_SESSION_ROAS'],
              mobSessionRoas.type,
              mobSessionRoas.format
            )}
            item2={makeRowColumn(
              rowObj['TRGT_MOB_SESSION_ROAS'],
              trgtMobSessionRoas.type,
              trgtMobSessionRoas.format
            )}
            item3={makeRowColumn(
              rowObj['MOB_SESSION_ORDER_AMT'],
              mobSessionOrderAmt.type,
              mobSessionOrderAmt.format
            )}
          />
        );
      },
    },
    TRGT_MOB_SESSION_ROAS: {
      merged: true,
    },
    MOB_SESSION_ORDER_AMT: {
      merged: true,
    },
    SESSION_ROAS: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={
                  getHeadObj(headsMap, 'SESSION_ORDER_AMT').label +
                  '(' +
                  t('쇼핑') +
                  ')'
                }
                tooltipType={'SESSION_ROAS'}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const sessionRoas = getHeadObj(headsMap, 'SESSION_ROAS');
        const trgtSessionRoas = getHeadObj(headsMap, 'TRGT_SESSION_ROAS');
        const sessionOrderAmt = getHeadObj(headsMap, 'SESSION_ORDER_AMT');
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(
              rowObj['SESSION_ROAS'],
              sessionRoas.type,
              sessionRoas.format
            )}
            item2={makeRowColumn(
              rowObj['TRGT_SESSION_ROAS'],
              trgtSessionRoas.type,
              trgtSessionRoas.format
            )}
            item3={makeRowColumn(
              rowObj['SESSION_ORDER_AMT'],
              sessionOrderAmt.type,
              sessionOrderAmt.format
            )}
          />
        );
      },
    },
    TRGT_SESSION_ROAS: {
      merged: true,
    },
    SESSION_ORDER_AMT: {
      merged: true,
    },
    DIRECT_ROAS: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={
                  getHeadObj(headsMap, 'DIRECT_ORDER_AMT').label +
                  '(' +
                  t('쇼핑') +
                  ')'
                }
                tooltipType={'DIRECT_ROAS'}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const directRoas = getHeadObj(headsMap, 'DIRECT_ROAS');
        const trgtDirectRoas = getHeadObj(headsMap, 'TRGT_DIRECT_ROAS');
        const directOrderAmt = getHeadObj(headsMap, 'DIRECT_ORDER_AMT');
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(
              rowObj['DIRECT_ROAS'],
              directRoas.type,
              directRoas.format
            )}
            item2={makeRowColumn(
              rowObj['TRGT_DIRECT_ROAS'],
              trgtDirectRoas.type,
              trgtDirectRoas.format
            )}
            item3={makeRowColumn(
              rowObj['DIRECT_ORDER_AMT'],
              directOrderAmt.type,
              directOrderAmt.format
            )}
          />
        );
      },
    },
    TRGT_DIRECT_ROAS: {
      merged: true,
    },
    DIRECT_ORDER_AMT: {
      merged: true,
    },
    ROAS: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={
                  getHeadObj(headsMap, 'ORDER_AMT').label +
                  '(' +
                  t('쇼핑') +
                  ')'
                }
                tooltipType={'ROAS'}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const roas = getHeadObj(headsMap, 'ROAS');
        const trgtRoas = getHeadObj(headsMap, 'TRGT_ROAS');
        const orderAmt = getHeadObj(headsMap, 'ORDER_AMT');
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(rowObj['ROAS'], roas.type, roas.format)}
            item2={makeRowColumn(
              rowObj['TRGT_ROAS'],
              trgtRoas.type,
              trgtRoas.format
            )}
            item3={makeRowColumn(
              rowObj['ORDER_AMT'],
              orderAmt.type,
              orderAmt.format
            )}
          />
        );
      },
    },
    TRGT_ROAS: {
      merged: true,
    },
    ORDER_AMT: {
      merged: true,
    },
    ECPM: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={
                  getHeadObj(headsMap, 'ECPM').label + '(' + t('쇼핑') + ')'
                }
                tooltipType={'ECPM'}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const ecpm = getHeadObj(headsMap, 'ECPM');
        const trgtEcpm = getHeadObj(headsMap, 'TRGT_ECPM');
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(rowObj['ECPM'], ecpm.type, ecpm.format)}
            item2={makeRowColumn(
              rowObj['TRGT_ECPM'],
              trgtEcpm.type,
              trgtEcpm.format
            )}
          />
        );
      },
    },
    TRGT_ECPM: {
      merged: true,
    },
  };

  const tps = [
    'KL',
    'TC',
    'CW',
    'SP',
    'RC',
    'SR',
    'PR',
    'ST',
    'HU',
    'RM',
    'UM',
    'GG',
    'GS',
    'MM',
    'IB',
    'KM',
    'AU',
    'IK',
    'MK',
    'CM',
    'MR',
    'AD',
    'PB',
    'PE',
    'SA',
    'SJ',
    'AT',
    'EX',
  ];
  for (let i = 0; tps.length > i; i++) {
    let keyName = tps[i] + '_PAR_EPRS_RATE';
    customized[keyName] = { merged: true };
    keyName = tps[i] + '_ECPM';
    customized[keyName] = { merged: true };
    keyName = tps[i] + '_ADVRTS_AMT';
    customized[keyName] = { merged: true };
    keyName = tps[i] + '_AVG_ADVRTS_AMT';
    customized[keyName] = { merged: true };
    keyName = tps[i] + '_CLICK_RATE';
    customized[keyName] = { merged: true };

    keyName = tps[i] + '_PAR_EPRS_CNT';
    customized[keyName] = {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              {i18next.t(tps[i])}({tps[i]})
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <>
            <span
              className="black"
              title={getHeadObj(headsMap, tps[i] + '_PAR_EPRS_CNT').label}
            >
              {makeRowColumn(
                rowObj[tps[i] + '_PAR_EPRS_CNT'],
                'number',
                '#,##0'
              )}
            </span>
            <span
              className="black"
              title={tps[i] + getHeadObj(headsMap, 'PAR_EPRS_RATE').label}
            >
              (
              {makeRowColumn(
                rowObj[tps[i] + '_PAR_EPRS_RATE'],
                'number',
                '0.0%'
              )}
              )
            </span>
            <br />
            <span className={'green'} title={tps[i] + ' ECPM'}>
              {makeRowColumn(rowObj[tps[i] + '_ECPM'], 'number', '#,##0')}
            </span>
            <br />
            <span className={'red'} title={tps[i] + ' ' + t('지출금액')}>
              {makeRowColumn(rowObj[tps[i] + '_ADVRTS_AMT'], 'number', '#,##0')}
            </span>
            <br />
            <span className="black" title={tps[i] + ' ' + t('평균지출')}>
              {makeRowColumn(
                rowObj[tps[i] + '_AVG_ADVRTS_AMT'],
                'number',
                '#,##0.0'
              )}
            </span>
            <br />
            <span className={'blue'} title={tps[i] + ' CTR'}>
              {makeRowColumn(
                rowObj[tps[i] + '_CLICK_RATE'],
                'number',
                '0.000%'
              )}
            </span>
          </>
        );
      },
    };
  }

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          externalOption={{ type: 'checkBox' }}
          pltfomTpCodeDetailFilter={['04']}
        />
      </NewFilterWrap>

      <>
        <TargetingGraph params={{ ...paramConverter(params) }} />
        {/*<TableContainer*/}
        {/*  dataReqPromise={dailyTargeting}*/}
        {/*  params={{ ...params }}*/}
        {/*  sort="desc"*/}
        {/*  order="STATS_DTTM"*/}
        {/*  dense={true}*/}
        {/*  customized={customized}*/}
        {/*  hasExcel*/}
        {/*  expandTable*/}
        {/*  makeSubParam={makeSubParam}*/}
        {/*  childColumnSet={childColumnSet}*/}
        {/*  noneSearch*/}
        {/*/>*/}

        <NewTableContainer
          params={params}
          //loading={loading.table}
          //callBack={tableCallBack}
          getParam={getParam}
          dataReqPromise={dailyTargeting}
          customized={customized}
          expandTable
          rowsPerPage={-1}
          useFetch={false}
          useTotal={false}
          useSearch={false}
          usePagenation={false}
        />
      </>
    </>
  );
};

export default withRouter(Targeting);
